/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Alert, Spin } from 'antd'
import BaseModal from 'src/components/ts-components/BaseModal'
import { formatShortDate } from '@sportninja/common/ts-utils/date-utils'
import type { BatchLockPreview } from '@sportninja/common/services/teamBalance'
import colors from '@sportninja/common/constants/appColors'
import { formatCurrency } from '@sportninja/common/currency'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import Price from 'src/components/ts-components/Price'
import {
  paragraphSmall,
  paragraphXSmall,
  weightSemiBold,
} from 'src/components/css'
import { dollarsToMinor } from '../utils/balanceMath'
import { balanceLabel, balanceText } from '../utils/balanceI18n'

type BatchInvoiceModalProps = {
  open: boolean
  loading: boolean
  preview: BatchLockPreview | null
  error: string | null
  currencyCode: string
  submitting: boolean
  onConfirm: (locks: { slot_id: string; preview_fingerprint: string }[]) => void
  onClose: () => void
}

/**
 * SN-4709 (James, Jul 31) — confirm "Send N Invoices".
 *
 * The same principle James applied to Notify ("the problem is they don't know
 * who they're about to remind"): naming a count is not consent. Every player
 * about to be invoiced is listed with their share, how many payments the invoice
 * will request and when the first one is due — all of it the SERVER's own
 * schedule, per slot, exactly as the single-slot modal shows it.
 *
 * Slots that cannot be invoiced right now are listed separately rather than
 * silently dropped, so "I selected six and five went out" is explained on screen
 * before the rep confirms, not discovered afterwards.
 *
 * Tax on top: each player's pre-tax share, the tax added to it and the invoiced
 * total are separate columns (the SHARE column used to show the total), and the
 * footer totals all three. An older server sends totals only; the table then
 * keeps its previous single money column.
 */
const BatchInvoiceModal = ({
  open,
  loading,
  preview,
  error,
  currencyCode,
  submitting,
  onConfirm,
  onClose,
}: BatchInvoiceModalProps) => {
  const rows = preview?.slots ?? []
  const sendable = rows.filter((row) => row.error === null && row.fingerprint)
  const blocked = rows.filter((row) => row.error !== null)
  const summary = preview?.summary ?? null
  const hasTaxBreakdown =
    summary !== null &&
    summary.subtotal !== undefined &&
    summary.tax !== undefined

  return (
    <BaseModal
      isOpen={open}
      title={balanceLabel('common:balanceBatchInvoiceTitle')}
      onClose={onClose}
      destroyOnClose
      desktopWidth='620px'
      actions={
        <div css={footerStyle}>
          <HeaderActionButton
            variant='secondary'
            size='small'
            textWeight='regular'
            text={balanceLabel('common:balanceCommsCancel')}
            onClick={onClose}
            disabled={submitting}
            data-testid='batch-invoice-cancel'
          />
          <HeaderActionButton
            variant='primary'
            size='small'
            textWeight='regular'
            text={balanceLabel('common:balanceBatchInvoiceSend')}
            // Nothing sendable ⇒ nothing to confirm. The fingerprint is what
            // makes each invoice non-blind, so a row without one is never sent.
            disabled={loading || sendable.length === 0}
            isLoading={submitting}
            onClick={() =>
              onConfirm(
                sendable.map((row) => ({
                  slot_id: row.slot_id,
                  preview_fingerprint: row.fingerprint as string,
                }))
              )
            }
            data-testid='batch-invoice-submit'
          />
        </div>
      }
    >
      {loading ? (
        <div css={spinnerStyle} data-testid='batch-invoice-loading'>
          <Spin />
        </div>
      ) : error ? (
        <Alert
          type='warning'
          showIcon
          message={error}
          data-testid='batch-invoice-error'
        />
      ) : (
        <div css={bodyStyle}>
          <p css={introStyle}>
            {balanceText('common:balanceBatchInvoiceIntro')}
          </p>
          <table css={tableStyle} data-testid='batch-invoice-table'>
            <thead>
              <tr>
                <th>{balanceLabel('common:balanceBatchInvoiceColPlayer')}</th>
                {hasTaxBreakdown ? (
                  <>
                    <th css={numericStyle}>
                      {balanceLabel('common:balanceBatchInvoiceColShare')}
                    </th>
                    <th css={numericStyle}>
                      {balanceLabel('common:balanceColTax')}
                    </th>
                    <th css={numericStyle}>
                      {balanceLabel('common:balanceColTotal')}
                    </th>
                  </>
                ) : (
                  <th>{balanceLabel('common:balanceBatchInvoiceColShare')}</th>
                )}
                <th>{balanceLabel('common:balanceBatchInvoiceColPayments')}</th>
                <th>{balanceLabel('common:balanceBatchInvoiceColFirstDue')}</th>
              </tr>
            </thead>
            <tbody>
              {sendable.map((row) => (
                <tr key={row.slot_id} data-testid={`batch-row-${row.slot_id}`}>
                  <td>{row.player_name ?? '—'}</td>
                  {hasTaxBreakdown ? (
                    <>
                      <td
                        css={numericStyle}
                        data-testid={`batch-row-share-${row.slot_id}`}
                      >
                        <Price
                          amount={dollarsToMinor(row.subtotal ?? 0)}
                          currency={currencyCode}
                        />
                      </td>
                      <td
                        css={numericStyle}
                        data-testid={`batch-row-tax-${row.slot_id}`}
                      >
                        <Price
                          amount={dollarsToMinor(row.tax ?? 0)}
                          currency={currencyCode}
                        />
                      </td>
                    </>
                  ) : null}
                  <td
                    css={numericStyle}
                    data-testid={`batch-row-total-${row.slot_id}`}
                  >
                    <Price
                      amount={dollarsToMinor(row.total)}
                      currency={currencyCode}
                    />
                  </td>
                  <td css={numericStyle}>{row.payment_count}</td>
                  <td css={numericStyle}>
                    {row.first_due_date
                      ? formatShortDate(row.first_due_date)
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div css={totalStyle} data-testid='batch-invoice-total'>
            <span>{balanceLabel('common:balanceBatchInvoiceTotal')}</span>
            {hasTaxBreakdown ? (
              /* "$1,200.00 + $60.00 tax = $1,260.00" — the server's own summary. */
              <span data-testid='batch-invoice-total-breakdown'>
                {balanceText('common:balanceBatchInvoiceTotalBreakdown', {
                  share: formatCurrency({
                    amount: dollarsToMinor(summary?.subtotal ?? 0),
                    currencyCode,
                  }),
                  tax: formatCurrency({
                    amount: dollarsToMinor(summary?.tax ?? 0),
                    currencyCode,
                  }),
                  total: formatCurrency({
                    amount: dollarsToMinor(summary?.total ?? 0),
                    currencyCode,
                  }),
                })}
              </span>
            ) : (
              <Price
                amount={dollarsToMinor(summary?.total ?? 0)}
                currency={currencyCode}
              />
            )}
          </div>
          {blocked.length > 0 ? (
            <div css={blockedStyle} data-testid='batch-invoice-blocked'>
              <span css={blockedTitleStyle}>
                {balanceText('common:balanceBatchInvoiceBlocked', {
                  count: blocked.length,
                })}
              </span>
              <ul>
                {blocked.map((row) => (
                  <li key={row.slot_id}>
                    {row.player_name ?? '—'} — {row.error}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <p css={noteStyle}>{balanceText('common:balanceLockTaxNote')}</p>
        </div>
      )}
    </BaseModal>
  )
}

export default BatchInvoiceModal

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const spinnerStyle = css`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const introStyle = css`
  margin: 0;
  ${paragraphSmall};
  color: ${colors.NEUTRAL_0};
`

/**
 * A roster of up to 60 slots can be selected at once, so the list scrolls inside
 * the modal rather than pushing the confirm button off the bottom of a viewport.
 */
const tableStyle = css`
  width: 100%;
  border-collapse: collapse;
  display: block;
  max-height: 300px;
  overflow-y: auto;

  th,
  td {
    padding: 8px 4px;
    /* SECONDARY_300 is the modal's own body colour — the rules were invisible. */
    border-bottom: 1px solid ${colors.SECONDARY_100};
    text-align: left;
    color: ${colors.NEUTRAL_0};
  }

  th {
    ${paragraphXSmall};
    ${weightSemiBold};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: ${colors.NEUTRAL_50};
    position: sticky;
    top: 0;
    background: ${colors.SECONDARY_300};
  }
`

/*
 * Two classes on the same cell: the table's own th/td rule is more specific than
 * this one alone, so it is doubled to win without an !important.
 */
const numericStyle = css`
  && {
    text-align: right;
  }
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

const totalStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  ${weightSemiBold};
  font-variant-numeric: tabular-nums;
`

const blockedStyle = css`
  ${paragraphXSmall};
  color: ${colors.WARNING_300};

  ul {
    margin: 4px 0 0;
    padding-left: 18px;
  }
`

const blockedTitleStyle = css`
  ${weightSemiBold};
`

const noteStyle = css`
  margin: 0;
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_70};
`
